// store/modules/paymentSlips/late.js
import enums from '@/utils/enums';
import requestService from '@/services/request';
import { createTrace } from '@/services/logTrace';
import {
  CONFIG,
  NOTIFICATIONS,
  ROLES,
  SOLICITATE_TYPE,
  PARCEL_TYPES,
  USER,
  UNIT,
} from '../../../utils/constants';
import moment from 'moment-timezone';

const state = {
  hasLateSlips: false,
  loadingItems: false,
  loadingSolicitations: false,
  lateSlipsCache: {
    unitKey: null,
    validUntil: null,
    data: null,
  },
  inDelaySolicitationAllowed: false,
  hasLateReadySolicitations: false,
  lateSolicitationsCache: {
    unitKey: null,
    validUntil: null,
    data: null,
  },
};

const getters = {
  inDelaySolicitationAllowed: (state) => state.inDelaySolicitationAllowed,
  hasLateReadySolicitations: (state) => state.hasLateReadySolicitations,
  hasLateSlips: (state) => state.hasLateSlips,
  latePositionData: (state) => state?.lateSlipsCache?.data?.items || {},
  solicitations: (state) => state?.lateSolicitationsCache?.data?.items || {},
};

const mutations = {
  SET_DELAY_ALLOWED(state, value) {
    state.inDelaySolicitationAllowed = value;
  },
  SET_LATE_READY_SOLICITATIONS(state, value) {
    state.hasLateReadySolicitations = value;
  },
  SET_LATE_SOLICITATIONS_CACHE(state, { unitKey, validUntil, data }) {
    state.lateSolicitationsCache = { unitKey, validUntil, data };
  },
  CLEAR_LATE_SOLICITATIONS_CACHE(state) {
    state.lateSolicitationsCache.validUntil = null;
  },
  SET_LATE_SLIPS_CACHE(state, { unitKey, validUntil, data }) {
    state.lateSlipsCache = { unitKey, validUntil, data };
  },
  CLEAR_LATE_SLIPS_CACHE(state) {
    state.lateSlipsCache.validUntil = null;
  },
};

const actions = {
  getAllowedContracts({ rootGetters }, { module, contracts }) {
    const navigationRules = rootGetters[CONFIG.GET_NAVIGATION_RULES];
    const notAllowedContracts =
      navigationRules?.financial?.notAllowedContracts || {};
    const notAlloweds = notAllowedContracts[module] || [];

    return contracts.filter((contract) => !notAlloweds.includes(contract.type));
  },

  async getLateSolicitations(
    { commit, rootState, state, rootGetters, dispatch },
    contracts = null
  ) {
    const selectedUnit = rootState.unit.selectedUnit;
    const unitKey = selectedUnit ? selectedUnit.unit.id : null;
    if (state.loadingSolicitations) {
      return;
    }
    // Checagem de cache -- se a unidade não mudou e ainda está dentro do tempo de validade
    if (
      state.lateSolicitationsCache.unitKey === unitKey &&
      state.lateSolicitationsCache.validUntil &&
      moment().isBefore(state.lateSolicitationsCache.validUntil)
    ) {
      return state.lateSolicitationsCache.data.items;
    }
    try {
      if (rootGetters[USER.GET_USER_ROLE] !== ROLES.CLIENT_ROLE) return;
      state.loadingSolicitations = true;
      if (!contracts) {
        if (!selectedUnit) return;
        const { LATE_SLIPS } = enums.financialModules;
        contracts = await dispatch('getAllowedContracts', {
          module: LATE_SLIPS,
          contracts: selectedUnit.contracts,
        });
      }

      const { sku } = selectedUnit;
      const params = {
        productSku: sku,
        contractIds: contracts.map((o) => o.id),
        contractTypes: contracts.map((o) => o.type),
        solicitationType: 'Late',
      };
      const url = `${process.env.FINANCIAL_API_URL}/solicitation/query`;

      // Busca via API
      const { items, solicitationAllowed } = await requestService(url, 'GET', {
        params,
        localErrorHandling: true,
      });

      commit('SET_DELAY_ALLOWED', solicitationAllowed);

      const cacheValidUntil = moment().add(2, 'minutes').toISOString();
      commit('SET_LATE_SOLICITATIONS_CACHE', {
        unitKey,
        validUntil: cacheValidUntil,
        data: { items, solicitationAllowed },
      });

      if (items) {
        const logSolicitations = [];

        const readySolicitations = items.reduce((ready, o) => {
          if (o.ready) {
            const [item] = o.items;
            ready.push(item);
          }
          if (o.logError || o.logGenerated || o.logExpired) {
            logSolicitations.push(o);
          }
          return ready;
        }, []);

        for (const solicitation of logSolicitations) {
          const slipContract = selectedUnit.contracts.find(
            (contract) => contract.id === solicitation.contractId
          );
          const title = dispatch(UNIT.GET_CONTRACT_TITLE, slipContract, {
            root: true,
          });
          const solicitationDetails = solicitation.items[0];
          if (slipContract && solicitationDetails) {
            const {
              paymentTotal,
              solicitationCode,
              expirationDate,
              parcelIds,
            } = solicitationDetails;
            if (solicitation.logError || solicitation.logExpired) {
              const traceAction = solicitation.logError
                ? enums.logTraceActions.latePaymentSlipSolicitationError
                : enums.logTraceActions.latePaymentSlipSolicitationExpired;
              const traceInfo = {
                contractId: slipContract.id,
                contractType: slipContract.type,
                detachedType: title,
                paymentTotal,
                solicitationCode,
                expirationDate,
                parcels: parcelIds,
              };
              createTrace(traceAction, traceInfo);
            } else if (solicitation.logGenerated) {
              const traceInfo = {
                contractId: slipContract.id,
                contractType: slipContract.type,
                detachedType: title,
                paymentTotal,
                solicitationCode,
                expirationDate,
                parcels: parcelIds,
              };
              const traceAction =
                enums.logTraceActions.latePaymentSlipSolicitationReady;
              createTrace(traceAction, traceInfo);
            }
          }
        }

        if (readySolicitations.length > 0) {
          commit(NOTIFICATIONS.MUTATE_LATE_PAYMENT_SLIPS, readySolicitations, {
            root: true,
          });
          state.hasLateReadySolicitations = readySolicitations.some(
            (item) => item.solicitationType === SOLICITATE_TYPE.LATE
          );
        }

        return items;
      }
    } catch (err) {
      throw err;
    } finally {
      state.loadingSolicitations = false;
    }
  },

  async getLatePaymentSlips(
    { commit, rootState, state, rootGetters, dispatch },
    contracts
  ) {
    try {
      const selectedUnit = rootState.unit.selectedUnit;
      const unitKey = selectedUnit ? selectedUnit.unit.id : null;

      if (
        state.lateSlipsCache.unitKey === unitKey &&
        state.lateSlipsCache.validUntil &&
        moment().isBefore(state.lateSlipsCache.validUntil)
      ) {
        return state.lateSlipsCache.data.items;
      }

      if (!contracts) {
        if (!selectedUnit) return;
        const { LATE_SLIPS } = enums.financialModules;
        contracts = await dispatch('getAllowedContracts', {
          module: LATE_SLIPS,
          contracts: selectedUnit.contracts,
        });
      }

      const promises = [];
      const url = `${process.env.FINANCIAL_API_URL}/late`;

      state.hasLateSlips = false;

      let loopContracts = (contracts, action) => {
        for (const contract of contracts) {
          action(contract);
        }
      };

      const lateSlipsByContract = {};

      loopContracts(contracts, (contract) => {
        const body = {
          contractId: contract.id,
          contractType: contract.type,
        };
        lateSlipsByContract[contract.id] = null;
        promises.push(
          requestService(url, 'GET', {
            params: body,
            localErrorHandling: true,
          }).then((res) => {
            const contractId = contract.id;
            if (lateSlipsByContract[contractId] === null) {
              lateSlipsByContract[contractId] = { late: [] };
            }
            // lateSlipsByContract[contractId] = [];
            if (res && res.items.length > 0) {
              const parcels = res.items.map((o) => ({ contractId, ...o }));
              lateSlipsByContract[contractId] = { late: parcels };
              // lateSlipsByContract = { ...lateSlipsByContract };
              if (!state.hasLateSlips) state.hasLateSlips = true;
              state.loadingItems = false;
            }
            return res;
          })
        );
      });

      await Promise.all(promises);

      loopContracts(contracts, (contract) => {
        let index = lateSlipsByContract[contract.id]['late'].findIndex(
          (slip) => slip.type === PARCEL_TYPES.proSolutoPos
        );

        if (index > -1) {
          let result = lateSlipsByContract[contract.id]['late'].filter(
            (slip) => slip.type != PARCEL_TYPES.proSolutoPos
          );

          if (result.length === 0) {
            state.parcelTypesNotValid = true;
          } else {
            lateSlipsByContract[contract.id]['late'] = result;
          }
        }
      });
      const cacheValidUntil = moment().add(2, 'minutes').toISOString();
      const newCache = {
        unitKey,
        validUntil: cacheValidUntil,
        data: { items: { ...lateSlipsByContract } },
      };
      commit('SET_LATE_SLIPS_CACHE', newCache);

      return lateSlipsByContract;
    } catch (err) {
      console.error('Error fetching late payment slips:', err);
      throw err;
    }
  },

  resetLateSlipsCache({ commit, dispatch }) {
    commit('CLEAR_LATE_SLIPS_CACHE');
    commit('CLEAR_LATE_SOLICITATIONS_CACHE');
  },

  async sendSlipByEmail({ rootState }, { item, email, fromHome = false }) {
    const selectedUnit = rootState.unit.selectedUnit;
    const contractId = item.contractId;

    const data = {
      contractId,
      productCode: selectedUnit.product.code,
      blockCode: selectedUnit.block.code,
      unitCode: selectedUnit.unit.code,
      documentCode: item.documentCode,
      solicitationType: 'late',
      userEmail: email,
    };

    const url = process.env.SLIP_CHECK_FUNCTION;
    const res = await requestService(`${url}/sendSlipEmail`, 'POST', {
      data,
      useLoading: false,
      localErrorHandling: true,
    });

    const { emailSended } = res;
    if (emailSended) {
      const slipContract = selectedUnit.contracts.find(
        (contract) => contract.id === contractId
      );
      const traceInfo = {
        contractId: contractId,
        contractType: slipContract.type,
        detachedType: slipContract.detachedType,
        expirationDate: item.expirationDate,
        serieId: item.serieId,
        serieCode: item.serieCode,
        parcelCode: item.parcelCode,
        discountedValue: item.discountedValue,
        ready: item.ready,
        url: item.slipUrl,
      };
      const { logTraceActions } = enums;
      const traceAction =
        logTraceActions[`latePaymentSlipEmail${fromHome ? 'Home' : ''}`];
      createTrace(traceAction, traceInfo);
    } else {
      console.error('Payment slip not ready');
    }
    return res;
  },
  async sendSlipBySms({ rootState }, { item, cellphone, fromHome = false }) {
    const selectedUnit = rootState.unit.selectedUnit;
    const contractId = item.contractId;

    const data = {
      contractId,
      productId: selectedUnit.product.id,
      blockCode: selectedUnit.block.code,
      unitCode: selectedUnit.unit.code,
      documentCode: item.documentCode,
      smsType: 'lateSlipRequested',
      userPhoneNumber: cellphone,
    };

    const url = process.env.SMS_API;
    return requestService(`${url}/addToQueue`, 'POST', {
      data,
      useLoading: false,
      localErrorHandling: true,
    })
      .then((res) => {
        const { count } = res;
        if (count) {
          const slipContract = selectedUnit.contracts.find(
            (contract) => contract.id === contractId
          );
          const traceInfo = {
            contractId: contractId,
            contractType: slipContract.type,
            detachedType: slipContract.detachedType,
            expirationDate: item.expirationDate,
            serieId: item.serieId,
            serieCode: item.serieCode,
            parcelCode: item.parcelCode,
            discountedValue: item.discountedValue,
            ready: item.ready,
            url: item.slipUrl,
            barcode: item.barcode,
            userCellphone: cellphone,
          };
          const { logTraceActions } = enums;
          const traceAction =
            logTraceActions[`latePaymentSlipSms${fromHome ? 'Home' : ''}`];
          createTrace(traceAction, traceInfo);
        }
        return res;
      })
      .catch((err) => {
        throw err;
      });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};



// WEBPACK FOOTER //
// ./src/store/modules/paymentSlips/lateSlips.js